import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { ResponsiveContainer, PieChart, Pie, Tooltip, Cell } from 'recharts';
import { getCategoryColor } from '../../utils/categoryColors';

type Item = { category: string; amount: number; type: 'income'|'expense'|'savings' };
type PieRow = { name: string; value: number };

const normalizeCategoryLabel = (label?: string): string => {
  const normalized = (label || '').trim().replace(/\s+/g, ' ');
  return normalized || 'Uncategorized';
};

const normalizeCategoryKey = (label: string): string => label.toLowerCase();

const truncateLabel = (value: string, maxLength = 22): string => (
  value.length > maxLength ? `${value.slice(0, maxLength - 1)}…` : value
);

const CATEGORY_PIE_TOP_N = 8;

const CategorySpendPie: React.FC<{ data: Item[] }> = ({ data }) => {
  const rows = useMemo(() => {
    const byCategory = new Map<string, PieRow>();
    for (const item of data) {
      if (item.type !== 'expense') continue;
      const label = normalizeCategoryLabel(item.category);
      const key = normalizeCategoryKey(label);
      const existing = byCategory.get(key);
      if (existing) {
        existing.value += item.amount;
      } else {
        byCategory.set(key, { name: label, value: item.amount });
      }
    }

    const sorted = Array.from(byCategory.values()).sort((a, b) => b.value - a.value);
    const topRows = sorted.slice(0, CATEGORY_PIE_TOP_N);
    const otherTotal = sorted.slice(CATEGORY_PIE_TOP_N).reduce((sum, row) => sum + row.value, 0);
    return otherTotal > 0 ? [...topRows, { name: 'Other', value: otherTotal }] : topRows;
  }, [data]);

  const total = rows.reduce((sum, row) => sum + row.value, 0);

  return (
    <Box sx={{ width: '100%', minHeight: 360, px: 1, pt: 0.5, pb: 1, overflow: 'hidden' }}>
      <Box sx={{ height: 220 }}>
        <ResponsiveContainer>
          <PieChart margin={{ top: 8, right: 16, left: 16, bottom: 8 }}>
            <Pie data={rows} dataKey="value" nameKey="name" label={false} labelLine={false}>
              {rows.map((row, i) => (
                <Cell key={`${row.name}-${i}`} fill={getCategoryColor(row.name)} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value, name, props) => {
                const rawValue = Array.isArray(value) ? value[0] : value;
                const amount = typeof rawValue === 'number' ? rawValue : Number(rawValue) || 0;
                const pct = total > 0 ? (amount / total) * 100 : 0;
                const rawLabel = props?.payload?.name ?? name ?? '';
                const label = String(Array.isArray(rawLabel) ? rawLabel[0] : rawLabel);
                return [`$${amount.toLocaleString(undefined, { maximumFractionDigits: 2 })} (${pct.toFixed(1)}%)`, label];
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </Box>

      <Box
        data-testid="category-pie-legend"
        data-export-expand="true"
        sx={{
          mt: 1,
          maxHeight: 130,
          overflowY: 'auto',
          pr: 0.5,
        }}
      >
        {rows.map((row) => (
          <Box
            key={`legend-${row.name}`}
            sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, py: 0.25 }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor: getCategoryColor(row.name),
                flexShrink: 0,
              }}
            />
            <Typography
              variant="body2"
              title={row.name}
              sx={{ minWidth: 0, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}
            >
              {truncateLabel(row.name)}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default CategorySpendPie;
